import {
    OverlayContainer,
    OverlayProvider,
    useOverlayPosition,
    useOverlayTrigger,
} from "@react-aria/overlays";
import {
    RangeCalendarWrapper,
    StyledBaseButton,
    StyledRangeDateFieldWrapper,
} from "./RangeCalendarMain.styles";
import React, { useEffect, useRef, useState } from "react";

import { DateRangeField } from "./DateRangeField";
import { FEEDBACK_TYPES } from "shared/config/feedbackTypes";
import HStack from "shared/components/layout/stack/HStack";
import Icon from "shared/components/utils/Icon";
import PropTypes from "prop-types";
import { RangeCalendar } from "./RangeCalendar";
import RangeCalendarPopOver from "./RangeCalendarPopOver";
import { parseDate } from "@internationalized/date";
import { useDateRangePicker } from "@react-aria/datepicker";
import { useDateRangePickerState } from "@react-stately/datepicker";
import { useOverlayTriggerState } from "@react-stately/overlays";

const RangeCalendarMain = ({
    onChange = () => void 0,
    onBlur = () => void 0,
    value,
    disabled = false,
    feedbackType,
    isSmallSize = false,
    confirmedChange,
    minDate,
    disableDays,
    enableDaysFromDate,
    enableDaysUntilDate,
    feedbackMessage,
    ...props
}) => {
    const ref = React.useRef();
    const dateRangeState = useDateRangePickerState(props);
    const [confirmedDateValue, setConfirmedDateValue] = useState(null);
    const {
        groupProps,
        startFieldProps,
        endFieldProps,
        buttonProps,
        dialogProps,
        calendarProps,
    } = useDateRangePicker(props, dateRangeState, ref);

    const overlayState = useOverlayTriggerState({});
    const triggerRef = React.useRef();
    const overlayRef = React.useRef();

    const { triggerProps, overlayProps } = useOverlayTrigger(
        { type: "dialog" },
        overlayState,
        triggerRef
    );

    const { overlayProps: positionProps } = useOverlayPosition({
        targetRef: triggerRef,
        overlayRef,
        placement: "bottom right",
        crossOffset: 9,
        isOpen: overlayState.isOpen,
    });

    // Latest-ref pattern: keep current versions of unstable callbacks/state methods
    // so the effect can call them without needing them as deps.
    const setDateRangeValueRef = useRef(dateRangeState.setValue);
    setDateRangeValueRef.current = dateRangeState.setValue;
    const confirmedChangeRef = useRef(confirmedChange);
    confirmedChangeRef.current = confirmedChange;
    // Track the last value we processed to avoid re-running on re-renders.
    const lastProcessedValueRef = useRef(null);

    const handleOnClickCalendar = () => {
        overlayState.setOpen(!overlayState.isOpen);
        dateRangeState.setOpen(!dateRangeState.isOpen);
    };

    useEffect(() => {
        if (value !== undefined && value && value !== lastProcessedValueRef.current) {
            if (value?.start && value?.end) {
                let date;
                if (value.start.calendar) {
                    date = value;
                } else {
                    date = {
                        start: parseDate(value.start),
                        end: parseDate(value.end),
                    };
                }
                lastProcessedValueRef.current = value;
                setDateRangeValueRef.current(date);
                setConfirmedDateValue(date);
                confirmedChangeRef.current(date);
            }
        }
    }, [value]);

    const handleOnClickCancel = () => {
        if (confirmedDateValue?.start && confirmedDateValue?.end) {
            dateRangeState.setValue(confirmedDateValue);
        } else {
            dateRangeState.setValue({ start: null, end: null });
        }
        overlayState.setOpen(false);
    };

    const onCloseCalendar = () => {
        confirmedNewDate();
    };

    const handleOnClickDone = (e) => {
        confirmedNewDate();
    };

    const confirmedNewDate = () => {
        setConfirmedDateValue(dateRangeState.value);
        onChange(dateRangeState.value);
        confirmedChange(dateRangeState.value);
        onBlur(dateRangeState.value);
        overlayState.setOpen(false);
    };

    const handleInputBlur = () => {
        const date = { start: startFieldProps.value, end: endFieldProps.value };
        dateRangeState.setValue(date);
        setConfirmedDateValue(dateRangeState.value);
        onChange(dateRangeState.value);
        onBlur(dateRangeState.value);
        confirmedChange(dateRangeState.value);
    };

    return (
        <RangeCalendarWrapper spacing={"Zero_25"}>
                <StyledRangeDateFieldWrapper
                    {...groupProps}
                    onBlur={handleInputBlur}
                    ref={ref}
                    feedbackType={
                        !overlayState.isOpen && feedbackMessage
                            ? feedbackType
                            : FEEDBACK_TYPES.None
                    }
                    disabled={disabled}
                    isSmallSize={isSmallSize}
                >
                    <HStack>
                        <DateRangeField
                            disabled={disabled}
                            isSmallSize={isSmallSize}
                            {...startFieldProps}
                        />
                        -
                        <DateRangeField
                            disabled={disabled}
                            isSmallSize={isSmallSize}
                            {...endFieldProps}
                        />
                    </HStack>

                    {disabled ? (
                        <StyledBaseButton disabled={disabled}>
                            <Icon name="calendar" />
                        </StyledBaseButton>
                    ) : (
                        <StyledBaseButton
                            type="button"
                            onClick={handleOnClickCalendar}
                            {...Object.entries(buttonProps).splice(
                                Object.keys(buttonProps).indexOf("onPress"),
                                1
                            )}
                            {...Object.entries(triggerProps).splice(
                                Object.keys(triggerProps).indexOf("onPress"),
                                1
                            )}
                            ref={triggerRef}
                        >
                            <Icon name="calendar" />
                        </StyledBaseButton>
                    )}
                </StyledRangeDateFieldWrapper>

                {!disabled && (
                    <OverlayProvider>
                        {overlayState.isOpen && (
                            <OverlayContainer>
                                    <RangeCalendarPopOver
                                        {...dialogProps}
                                        {...overlayProps}
                                        {...positionProps}
                                        ref={overlayRef}
                                        isOpen={overlayState.isOpen}
                                        onClose={onCloseCalendar}
                                    >
                                        <RangeCalendar
                                            disableDays={disableDays}
                                            enableDaysFromDate={enableDaysFromDate}
                                            enableDaysUntilDate={enableDaysUntilDate}
                                            minDate={minDate}
                                            onClickCancel={handleOnClickCancel}
                                            onClickDone={handleOnClickDone}
                                            {...calendarProps}
                                        />
                                    </RangeCalendarPopOver>
                            </OverlayContainer>
                        )}
                    </OverlayProvider>
                )}
        </RangeCalendarWrapper>
    );
};

RangeCalendarMain.propTypes = {
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.object,
    disabled: PropTypes.bool,
    feedbackType: PropTypes.string,
    isSmallSize: PropTypes.bool,
    confirmedChange: PropTypes.func,
    minDate: PropTypes.object,
    disableDays: PropTypes.array,
    enableDaysFromDate: PropTypes.number,
    enableDaysUntilDate: PropTypes.number,
    feedbackMessage: PropTypes.string,
};

export default RangeCalendarMain;
